import type { BookAt } from '@alphadesk/ontology/objects/book.ts';
import type { WireBook } from '@alphadesk/web/contract.ts';
import { PAGES } from '@alphadesk/web/links.ts';
import { useEffect, useSyncExternalStore } from 'react';
import { replaceQuery, useQueryState } from './router.ts';

export interface Scope {
  analystId: number | null;
  /** Only ever set alongside `analystId`: a book is one analyst's. */
  bookId: number | null;
}

export const NO_SCOPE: Scope = { analystId: null, bookId: null };

export const isScoped = (scope: Scope): boolean => scope.analystId !== null;

export function inScope(scope: Scope, ref: BookAt): boolean {
  if (!isScoped(scope)) return true;
  return (
    ref.analystId === scope.analystId && (scope.bookId === null || ref.bookId === scope.bookId)
  );
}

/** A note about no recommendation is outside every scope but none, unless it is an unread alarm. */
export const noteInScope = (
  scope: Scope,
  note: { about: BookAt | null; voice: string; readAt: string | null },
): boolean =>
  !isScoped(scope) ||
  (note.voice === 'alarm' && note.readAt === null) ||
  (note.about !== null && inScope(scope, note.about));

export function scopeName(scope: Scope, books: readonly WireBook[]): string {
  const analyst = books.find((entry) => entry.analystId === scope.analystId)?.analyst ?? 'Analyst';
  const book = books.find((entry) => entry.bookId === scope.bookId)?.book ?? null;
  return book === null ? analyst : `${analyst} · ${book}`;
}

const QUERY_KEYS = ['analyst', 'book'] as const;

const idIn = (q: URLSearchParams, key: string): number | null => {
  const id = Number(q.get(key));
  return Number.isSafeInteger(id) && id > 0 ? id : null;
};

function parseScope(search: string): Scope | null {
  const q = new URLSearchParams(search);
  if (q.get('analyst') === null) return null;
  const analystId = idIn(q, 'analyst');
  return analystId === null ? NO_SCOPE : { analystId, bookId: idIn(q, 'book') };
}

function serialiseScope(scope: Scope, search: string): string {
  const q = new URLSearchParams(search);
  for (const key of QUERY_KEYS) q.delete(key);
  if (isScoped(scope)) {
    q.set('analyst', String(scope.analystId));
    if (scope.bookId !== null) q.set('book', String(scope.bookId));
  }
  return q.toString();
}

const SCOPED_PAGES: readonly string[] = [PAGES.trades, PAGES.notifications];

let current: Scope =
  typeof window === 'undefined' ? NO_SCOPE : (parseScope(window.location.search) ?? NO_SCOPE);
const listeners = new Set<() => void>();

function write(): void {
  if (!SCOPED_PAGES.includes(window.location.pathname)) return;
  const next = serialiseScope(current, window.location.search);
  if (next !== window.location.search.replace(/^\?/, '')) replaceQuery(next);
}

export function setScope(next: Scope): void {
  const scope = isScoped(next) ? next : NO_SCOPE;
  const moved = scope.analystId !== current.analystId || scope.bookId !== current.bookId;
  if (moved) current = scope;
  // Written even when unmoved, so an address naming a book by anything but its id is rewritten.
  write();
  if (moved) for (const listener of listeners) listener();
}

const subscribe = (listener: () => void) => {
  listeners.add(listener);
  return () => listeners.delete(listener);
};

export const getScope = (): Scope => current;

export const useScope = (): Scope => useSyncExternalStore(subscribe, getScope);

/** A link that names a scope sets it; a page opened bare keeps the one already chosen. */
export function useScopeInAddress(path: string, search: string): void {
  useEffect(() => {
    if (!SCOPED_PAGES.includes(path)) return;
    const named = parseScope(search);
    if (named === null) write();
    else setScope(named);
  }, [path, search]);
}

const parseBook = (search: string): { bookId: number | null } => ({
  bookId: idIn(new URLSearchParams(search), 'book'),
});

function serialiseBook({ bookId }: { bookId: number | null }, search: string): string {
  const q = new URLSearchParams(serialiseScope(NO_SCOPE, search));
  if (bookId !== null) q.set('book', String(bookId));
  return q.toString();
}

export const useBookQuery = () => useQueryState(parseBook, serialiseBook);
